// components/ConfirmationModal.tsx
import React, { useCallback, useEffect, useRef } from 'react';
import type { ConfirmationModalPayload } from '#types/types';

const stopClickPropagation = (event: React.MouseEvent) => event.stopPropagation();

const TITLE_STYLE: React.CSSProperties = { fontSize: 18, lineHeight: '26px' };
const MESSAGE_STYLE: React.CSSProperties = { fontSize: 14, lineHeight: '22px' };
const BUTTON_STYLE: React.CSSProperties = { fontSize: 14 };

const ConfirmationModal: React.FC<{ payload: ConfirmationModalPayload; onClose: () => void }> = ({ payload, onClose }) => {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const confirm = useCallback(() => {
    onClose();
    payload.onConfirm();
  }, [payload, onClose]);

  // Capture phase: the canvas would otherwise also take Escape and deselect its tool.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      } else if (event.key === 'Tab') {
        event.preventDefault();
        (document.activeElement === cancelRef.current ? confirmRef : cancelRef).current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] flex animate-[fadeIn_140ms_ease-out_both] items-center justify-center bg-[#141414]/80 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        role="dialog" aria-modal="true" aria-label={payload.title}
        className="w-full max-w-[400px] rounded-[24px] border border-white/[0.06] bg-[#212121] p-6 shadow-[0_24px_60px_rgba(0,0,0,0.55)]"
        onClick={stopClickPropagation}
      >
        <h2 className="font-semibold text-[#ECECEC]" style={TITLE_STYLE}>{payload.title}</h2>
        <p className="mt-3 break-words text-[#AFAFAF]" style={MESSAGE_STYLE}>{payload.message}</p>
        <div className="mt-6 flex justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            autoFocus
            onClick={onClose}
            className="h-9 rounded-full border border-white/[0.12] px-4 font-medium text-[#ECECEC] transition-colors hover:bg-white/[0.08]"
            style={BUTTON_STYLE}
          >
            Cancel
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={confirm}
            className="h-9 rounded-full border border-[#F28B82]/40 px-4 font-medium text-[#F28B82] transition-colors hover:bg-[#F28B82]/10"
            style={BUTTON_STYLE}
          >
            {payload.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ConfirmationModal);
